import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Check, Zap, Crown, Star, ArrowLeft } from 'lucide-react';
import usePricingPlans from '../utils/usePricingPlans';

const iconFor = (plan, index) => {
  if (plan.name === 'Pro' || plan.popular) return Zap;
  if (plan.name === 'Premium' || index === 2) return Crown;
  return Star;
};

const PricingSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const plans = usePricingPlans();

  return (
    <section className="section-padding bg-orvix-navy/30" ref={ref}>
      <div className="container-custom mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orvix-green/10 border border-orvix-green/20 text-orvix-green text-sm font-medium mb-6">
            <Crown size={16} />
            <span>الباقات</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            اختر <span className="gradient-text">الباقة</span> المناسبة
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            باقات مرنة تناسب جميع الأحجام والأنشطة التجارية مع إمكانية الترقية في أي وقت
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, index) => {
            const PlanIcon = iconFor(plan, index);
            return (
              <div
                key={plan._id || plan.name || index}
                className={`relative glass p-8 transition-all duration-700 hover:-translate-y-2 ${
                  plan.popular ? 'lg:scale-105 border-2 ' + (plan.borderColor || 'border-orvix-primary/50') : ''
                } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-orvix-primary to-blue-600 text-white text-xs font-bold">
                    الأكثر طلباً
                  </div>
                )}

                <div className="text-center mb-8">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${plan.color || 'from-gray-500 to-gray-600'} flex items-center justify-center mx-auto mb-4`}>
                    <PlanIcon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-orvix-green text-sm font-medium mb-1">{plan.nameAr}</p>
                  <p className="text-gray-500 text-sm">{plan.desc}</p>
                </div>

                <div className="text-center mb-8 pb-8 border-b border-white/10">
                  {plan.price ? (
                    <>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl lg:text-5xl font-bold text-white">{plan.price}</span>
                        <span className="text-gray-400 text-sm">ر.س</span>
                      </div>
                      {plan.period && <span className="text-gray-500 text-sm">/ {plan.period}</span>}
                    </>
                  ) : (
                    <div className="text-2xl font-bold text-orvix-green">تواصل معنا للسعر</div>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {(plan.features || []).map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                      <Check size={16} className="text-orvix-green flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                  {(plan.disabled || []).map((feature, i) => (
                    <li key={`d-${i}`} className="flex items-start gap-3 text-gray-600 text-sm line-through">
                      <span className="w-4 flex-shrink-0 text-center">—</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-orvix-primary to-blue-600 text-white hover:shadow-lg hover:shadow-orvix-primary/30'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                  }`}
                >
                  اختر الباقة <ArrowLeft size={16} />
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            جميع الباقات تشمل <span className="text-orvix-green">ربط مع هيئة الزكاة والدخل</span> حسب السعر الموضح في صفحة الباقات
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
